import { useQuery } from '@tanstack/react-query';
import { ENTITY_KEY, ITEM_IMPORT_QUERY_KEY, LIST_KEY } from '../queryKeys';
import { queryYemboApiCall, queryYemboApiCallWithReturnValue } from '../queryYemboApiCalls';

/**
 * The item-import package: one batch of source files (photos, list photos or a
 * spreadsheet), extracted asynchronously into candidate items that a human
 * reviews before any of them enters the job.
 *
 * The contract is `service-voyager-api/app/src/routes/Item.ts`
 * (`/import-images`, `/import-lists`, `/import-spreadsheet`) and
 * `routes/ItemImport.ts` (poll, commit, delete). The iPad capture app
 * (`scanmyhome-ios`, Contents target) drives the same endpoints.
 */

const importUrl = 'item';
const packageUrl = 'item-import';

/**
 * Extraction is a per-file vision call, so a batch takes tens of seconds. Two
 * seconds keeps the processed count moving without hammering the API — the
 * same interval the iPad app polls at.
 */
export const IMPORT_POLL_INTERVAL_MS = 2000;

/**
 * How often a job page re-reads its in-progress spreadsheet imports while one
 * of them is moving (reading rows or pricing items) — often enough that the
 * banner's count and bar visibly advance, and the 'ready' state shows up
 * within a few seconds of the worker finishing.
 */
export const IMPORT_BANNER_ACTIVE_POLL_MS = 3000;

/**
 * And while nothing is moving. Still polled, only slowly: it is how an upload
 * started in another tab (or by a colleague) turns up on this job's pages
 * without a reload.
 */
export const IMPORT_BANNER_IDLE_POLL_MS = 30_000;

/**
 * Uploads are up to 100 photos and 400 MB, which is far past the 90 s the rest
 * of the app allows for a single-file upload.
 */
const IMPORT_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/** Mirrors `MAX_IMPORT_IMAGES` in the API's `utils/imports.ts`. */
export const MAX_IMPORT_FILES = 100;

export type ImportType = 'images' | 'lists' | 'spreadsheet';

export type ItemImportStatus = 'pending' | 'processing' | 'complete' | 'imported' | 'failed';

/**
 * The server-side pricing pass that follows a spreadsheet commit made with
 * `autoShop` (service-voyager-video's `ShopImport` command). Null when the
 * import was committed without it, or had nothing worth shopping for.
 */
export type ImportShoppingStatus = 'pending' | 'shopping' | 'complete' | 'failed';

/**
 * Where an import stands, as one word, worked out by the API from `status`
 * and `shoppingStatus` together:
 *
 * - `reading`  — the worker is still reading the sheet (pending/processing)
 * - `ready`    — extraction finished; waiting for a person to review it
 * - `pricing`  — committed, and the worker is shopping each item
 * - `done`     — committed, nothing left running
 * - `failed`   — either the read or the pricing pass failed
 *
 * `uploading` is in the API's contract for symmetry but never comes back from
 * it — an import that exists has, by definition, finished uploading. The
 * adjuster only uses it for its own in-flight upload.
 */
export type ImportProgressPhase = 'uploading' | 'reading' | 'ready' | 'pricing' | 'done' | 'failed';

/**
 * The progress the API computes onto every import it returns, so every client
 * (this app, the iPad app) shows the same count and the same ETA rather than
 * each estimating its own.
 */
export type ItemImportProgress = {
  phase: ImportProgressPhase;
  /** Rows read while reading, items attempted while pricing; null when not yet known. */
  done: number | null;
  total: number | null;
  /** 0–100 to one decimal; null while the total is unknown. */
  percent: number | null;
  /** Null when nothing is running or there is not enough to estimate from yet. */
  etaSeconds: number | null;
  estimatedCompletionAt: string | null;
};

/**
 * One extracted item awaiting review. Every field is optional because it is
 * whatever the extractor managed to read; the review UI edits them as text and
 * the API parses them back out on commit (`normalizeCommitItem`).
 */
export type ImportCandidate = {
  itemTitle?: string | null;
  itemNumber?: string | null;
  category?: string | null;
  description?: string | null;
  searchQuery?: string | null;
  brand?: string | null;
  condition?: string | null;
  resolution?: string | null;
  /** The extractor returns either a number or a formatted string. */
  purchasePrice?: string | number | null;
  yearOfPurchase?: string | number | null;
  expectedYears?: string | number | null;
  quantity?: string | number | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  boundingBox?: { xMin: number; xMax: number; yMin: number; yMax: number } | null;
  /** Provenance the extraction recorded; passed straight back on commit. */
  imageKey?: string | null;
  /** A list organized under room headings names a room per item. */
  roomKey?: string | null;
  /**
   * A spreadsheet "Room name" column names a room by text instead of by key.
   * Resolved against the job's existing rooms at commit time (case-insensitive,
   * trimmed); a name with no match gets a new room created for it.
   */
  roomName?: string | null;
};

export type ItemImport = {
  key: string;
  companyKey: string;
  jobKey?: string | null;
  roomKey?: string | null;
  type: ImportType;
  status?: ItemImportStatus | null;
  fileCount?: number | null;
  processedCount?: number | null;
  results?: ImportCandidate[] | null;
  resultCount?: number | null;
  itemCount?: number | null;
  sourceUrl?: string | null;
  failureReason?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string | null;

  /** Spreadsheet data rows the worker will read; set once it has found the header. */
  rowTotal?: number | null;
  /** Of those, how many it has read so far (blank rows included, so it reaches `rowTotal`). */
  rowsProcessed?: number | null;

  /** Whether the commit asked for the server-side pricing pass. */
  autoShop?: boolean | null;
  shoppingStatus?: ImportShoppingStatus | null;
  /** Created items with something to shop for (a photo, or any words). */
  shopTotal?: number | null;
  /** Items the pricing pass has attempted, priced or not. */
  shoppedCount?: number | null;
  /** Items that came out of the pricing pass with a price applied. */
  pricedCount?: number | null;
  shoppingStartedAt?: string | null;
  shoppingCompletedAt?: string | null;
  shoppingFailureReason?: string | null;

  /** Computed by the API on every response; absent only from an older API. */
  progress?: ItemImportProgress | null;
};

/** `complete` and `imported` both mean extraction is done; `failed` is terminal too. */
export const isImportSettled = (status: ItemImportStatus | null | undefined): boolean =>
  status === 'complete' || status === 'imported' || status === 'failed';

type ImportTarget = {
  /** The room the items are filed into. Preferred over `jobKey`. */
  roomKey?: string | null;
  /** Used when no room is known — the API resolves the company from the job. */
  jobKey?: string | null;
};

/**
 * The multipart body every import endpoint takes: the target, then every file
 * under the repeated `files` field name.
 */
const buildImportForm = ({ roomKey, jobKey }: ImportTarget, files: File[]): FormData => {
  const form = new FormData();
  if (roomKey) form.append('roomKey', roomKey);
  if (jobKey) form.append('jobKey', jobKey);
  for (const file of files) form.append('files', file, file.name);
  return form;
};

const postImport = async (path: string, form: FormData): Promise<ItemImport> => {
  // 201 with the import itself.
  const result = await queryYemboApiCallWithReturnValue<ItemImport>({
    url: `${importUrl}/${path}`,
    method: 'POST',
    body: form,
    shouldNotifyAndThrowError: true,
    errorOnNotFound: true,
    timeoutMs: IMPORT_UPLOAD_TIMEOUT_MS,
  });

  if (!result) throw new Error('The import did not start');

  return result;
};

/** Photos of belongings — the worker detects objects in each one. */
export const postImportImages = (target: ImportTarget, files: File[]) =>
  postImport('import-images', buildImportForm(target, files));

/** Photos of a handwritten or printed inventory list — the worker OCRs them. */
export const postImportLists = (target: ImportTarget, files: File[]) =>
  postImport('import-lists', buildImportForm(target, files));

/**
 * An .xlsx, .xls or .csv. `columnMapping` is `{ "Their Column": "voyagerItemField" }`;
 * omitting it lets the worker infer the mapping from the header row.
 *
 * `autoCategorize` asks the worker to classify any row the sheet left with no
 * category via Gemini, using its title/description. It has to be decided
 * before this upload — the worker runs it during extraction, not after — so
 * it can't be offered alongside the post-extraction "find replacement
 * prices" option the review step shows.
 */
export const postImportSpreadsheet = (
  target: ImportTarget,
  file: File,
  columnMapping?: Record<string, string> | null,
  autoCategorize?: boolean
) => {
  const form = buildImportForm(target, [file]);
  if (columnMapping && Object.keys(columnMapping).length > 0) {
    form.append('columnMapping', JSON.stringify(columnMapping));
  }
  if (autoCategorize) {
    form.append('autoCategorize', 'true');
  }
  return postImport('import-spreadsheet', form);
};

export const getItemImport = async (key: string): Promise<ItemImport> => {
  // `GET /item-import/:key` answers the import itself.
  const result = await queryYemboApiCallWithReturnValue<ItemImport>({
    url: `${packageUrl}/${key}`,
    errorOnNotFound: true,
    shouldNotifyAndThrowError: false,
  });

  if (!result) throw new Error('Import not found');

  return result;
};

/**
 * Polls one import while its extraction runs. Stops on its own once the record
 * settles, so the caller only has to decide when to start.
 */
export const useItemImportQuery = (key: string | null, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [ITEM_IMPORT_QUERY_KEY, ENTITY_KEY, key],
    queryFn: () => getItemImport(key ?? ''),
    enabled: !!key && options?.enabled !== false,
    refetchInterval: (query) => (isImportSettled(query.state.data?.status) ? false : IMPORT_POLL_INTERVAL_MS),
    // A single failed poll during a minutes-long extraction is usually a blip.
    retry: 3,
    gcTime: 0,
  });

/**
 * A job's spreadsheet imports that something should be shown for — still
 * reading, waiting for review, pricing, or failed in the last day and not yet
 * dismissed — newest first. The API leaves each one's `results` off this list
 * (a sheet can hold thousands of rows); the review step fetches them on its
 * own, from `GET /item-import/:key`, when someone opens it.
 */
export const getJobImportsInProgress = async (jobKey: string): Promise<ItemImport[]> => {
  const result = await queryYemboApiCallWithReturnValue<ItemImport[], 'itemImports'>({
    url: `${packageUrl}?jobKey=${encodeURIComponent(jobKey)}&inProgress=true`,
    key: 'itemImports',
    shouldNotifyAndThrowError: false,
  });

  return result ?? [];
};

/** The phase of an import, preferring the API's own word for it. */
export const getImportPhase = (itemImport: ItemImport): ImportProgressPhase | null =>
  itemImport.progress?.phase ?? null;

/** True while an import is reading rows or pricing items — i.e. the banner has something moving. */
export const isImportRunning = (itemImport: ItemImport): boolean => {
  const phase = getImportPhase(itemImport);
  return phase === 'reading' || phase === 'pricing';
};

export const jobImportsInProgressQueryKey = (jobKey: string) => [
  ITEM_IMPORT_QUERY_KEY,
  LIST_KEY,
  jobKey,
  'in-progress',
];

/**
 * Drives the import banner on every job tab. Polls quickly while an import is
 * moving and slowly otherwise (see the two intervals above), so a spreadsheet
 * that is reading or pricing can be watched from anywhere in the job — or not
 * watched at all: the work happens on the server whether or not this is
 * mounted.
 */
export const useJobImportsInProgressQuery = (jobKey: string | null | undefined) =>
  useQuery({
    queryKey: jobImportsInProgressQueryKey(jobKey ?? ''),
    queryFn: () => getJobImportsInProgress(jobKey ?? ''),
    enabled: !!jobKey,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isImportRunning) ? IMPORT_BANNER_ACTIVE_POLL_MS : IMPORT_BANNER_IDLE_POLL_MS,
    // A missed poll is caught up by the next one; nothing here is worth a toast.
    retry: 1,
  });

export type CommitImportResult = {
  itemImport: ItemImport;
  // biome-ignore lint/suspicious/noExplicitAny: the API's Item shape differs from the stale forked type
  items: any[];
  /** Rows the API refused — no title, or a create that failed. */
  skipped?: number;
};

/**
 * Turns the reviewed candidates into real items on the job.
 *
 * `autoShop` asks the API to price the new items afterwards, on the server
 * (the worker's `ShopImport` command): the response comes back as soon as the
 * items exist, with `itemImport.shoppingStatus` 'pending' if there was
 * anything to shop for, and the job's import banner follows the pricing from
 * there. This used to be a loop in the browser that kept the tab hostage for
 * the five to ten minutes a large sheet takes.
 *
 * Read with `queryYemboApiCall` rather than the unwrapping helper: this is the
 * one import endpoint whose body is an envelope (`itemImport` + `items` +
 * `skipped`), and unwrapping by key would throw away the created items.
 */
export const commitItemImport = async (
  key: string,
  items: ImportCandidate[],
  autoShop = false
): Promise<CommitImportResult> => {
  const response = await queryYemboApiCall({
    url: `${packageUrl}/${key}/commit`,
    method: 'POST',
    body: JSON.stringify({ items, autoShop }),
    shouldNotifyAndThrowError: true,
    timeoutMs: IMPORT_UPLOAD_TIMEOUT_MS,
  });

  return (await response.json()) as CommitImportResult;
};

/**
 * Discards an import the reviewer backed out of, or dismisses a ready or
 * failed one from the job's banner (the API soft-deletes it either way, and
 * refuses with a 409 while it is still pricing). Best effort — an uncommitted
 * import holds no items, so a failed cleanup costs nothing.
 */
export const deleteItemImport = (key: string) =>
  queryYemboApiCall({
    url: `${packageUrl}/${key}`,
    method: 'DELETE',
  });
